import {
  ReviewMissingDateFieldValues,
  ReviewQueueDateFieldValues,
  type TReviewFilterMode,
  type TReviewMissingDateField,
  type TReviewQueueDateField,
} from '@onesource/schemas';
import { z } from 'zod';

import { EMPTY_FILTER_VALUE, type TReviewFilterValue } from './review-filter-panel';
import type { TReviewView } from './review-status-tiles';

const VIEWS = ['waiting', 'in_progress', 'reviewed', 'rejected', 'all', 'not_submitted'] as const;
const loose = z.string().optional().catch(undefined);
const mode = z.enum(['include', 'exclude']).optional().catch(undefined);

/**
 * The Teacher Review page's state in its URL, so a link — a reminder, a bookmark,
 * a shared tab — opens the same view with the same filters. Bad values fall back to
 * the defaults instead of breaking the page. Names match the API's query names.
 */
export const ReviewSearchSchema = z.object({
  view: z.enum(VIEWS).optional().catch(undefined),
  search: loose,
  moduleIds: loose,
  moduleMode: mode,
  formIds: loose,
  formMode: mode,
  teacherIds: loose,
  teacherMode: mode,
  reviewTime: loose,
  reviewTimeMode: mode,
  progress: loose,
  progressMode: mode,
  due: loose,
  dueMode: mode,
  dateField: z
    .enum([...ReviewQueueDateFieldValues, ...ReviewMissingDateFieldValues])
    .optional()
    .catch(undefined),
  dateFrom: z.string().datetime({ offset: true }).optional().catch(undefined),
  dateTo: z.string().datetime({ offset: true }).optional().catch(undefined),
});
export type TReviewSearch = z.infer<typeof ReviewSearchSchema>;

/** The list filters the page holds, each with its labels for the button. */
export type TReviewListFilterKey = 'module' | 'form' | 'teacher' | 'reviewTime' | 'progress' | 'due';

/** URL names of each list filter: the ids and the mode. */
export const LIST_FILTER_PARAMS: Record<TReviewListFilterKey, { ids: keyof TReviewSearch; mode: keyof TReviewSearch }> =
  {
    module: { ids: 'moduleIds', mode: 'moduleMode' },
    form: { ids: 'formIds', mode: 'formMode' },
    teacher: { ids: 'teacherIds', mode: 'teacherMode' },
    reviewTime: { ids: 'reviewTime', mode: 'reviewTimeMode' },
    progress: { ids: 'progress', mode: 'progressMode' },
    due: { ids: 'due', mode: 'dueMode' },
  };

export type TReviewDateField = TReviewQueueDateField | TReviewMissingDateField;

/** A date range on one date column; ISO date-times, either end may be open. */
export interface TReviewDateRange {
  field: TReviewDateField;
  from?: string;
  to?: string;
}

export const isMissingDateField = (field: TReviewDateField): field is TReviewMissingDateField =>
  (ReviewMissingDateFieldValues as readonly string[]).includes(field);

/** The date column each view starts on. */
export const defaultDateField = (isMissingView: boolean): TReviewDateField => (isMissingView ? 'due' : 'submitted');

/** The page's filters from its URL. Labels are filled in later from the filter options. */
export const filtersFromSearch = (search: TReviewSearch): Record<TReviewListFilterKey, TReviewFilterValue> => {
  const entries = Object.entries(LIST_FILTER_PARAMS).map(([key, names]) => {
    const ids = ((search[names.ids] as string | undefined) ?? '').split(',').filter(Boolean);
    const filterMode = (search[names.mode] as TReviewFilterMode | undefined) ?? 'include';
    return [key, ids.length > 0 ? { ids, labels: {}, mode: filterMode } : EMPTY_FILTER_VALUE];
  });
  return Object.fromEntries(entries) as Record<TReviewListFilterKey, TReviewFilterValue>;
};

/** The URL for the page's state; defaults are left out so a plain link stays plain. */
export const searchFromState = (state: {
  view: TReviewView;
  search: string;
  filters: Record<TReviewListFilterKey, TReviewFilterValue>;
  /** The filters this view uses; the others stay out of its URL. */
  activeKeys: TReviewListFilterKey[];
  date: TReviewDateRange;
}): TReviewSearch => {
  const out: TReviewSearch = {};
  if (state.view !== 'waiting') {
    out.view = state.view;
  }
  if (state.search) {
    out.search = state.search;
  }
  for (const key of state.activeKeys) {
    const names = LIST_FILTER_PARAMS[key];
    const value = state.filters[key];
    if (value.ids.length > 0) {
      Object.assign(out, { [names.ids]: value.ids.join(','), [names.mode]: value.mode });
    }
  }
  if (state.date.from || state.date.to) {
    out.dateField = state.date.field;
    out.dateFrom = state.date.from;
    out.dateTo = state.date.to;
  }
  return out;
};

/** The same page state? Key order and empty values do not count. */
export const isSameSearch = (a: object, b: object): boolean => {
  const canonical = (value: object) =>
    JSON.stringify(
      Object.entries(value)
        .filter(([key, v]) => key in ReviewSearchSchema.shape && v !== undefined && v !== '')
        .sort(([x], [y]) => x.localeCompare(y)),
    );
  return canonical(a) === canonical(b);
};
